import apiClient from '@/api/client';
import type {
  BulkDeleteResponse,
  BulkRecomputeResponse,
  GeometryBatchResponse,
  RouteGeoJsonFeature,
  RouteIdsResponse,
  RouteRequest,
  RouteResponse,
  RouteSortDirection,
  RouteSortField,
  RouteVariantKey,
  RoutesListResponse,
} from '@/types/routes';

export async function createRoute(body: RouteRequest): Promise<RouteResponse> {
  const response = await apiClient.post<RouteResponse>('/api/v1/routes', body);
  return response.data;
}

export async function deleteRoute(routeId: string): Promise<void> {
  await apiClient.delete(`/api/v1/routes/${encodeURIComponent(routeId)}`);
}

export async function fetchRoute(routeId: string): Promise<RouteResponse> {
  const response = await apiClient.get<RouteResponse>(
    `/api/v1/routes/${encodeURIComponent(routeId)}`,
  );
  return response.data;
}

export async function fetchRouteGeometry(
  routeId: string,
  variant: RouteVariantKey = 'gis',
): Promise<RouteGeoJsonFeature> {
  const response = await apiClient.get<RouteGeoJsonFeature>(
    `/api/v1/routes/${encodeURIComponent(routeId)}/geometry`,
    { params: { variant } },
  );
  return response.data;
}

export interface GeometryBatchParams {
  readonly route_ids: readonly string[];
  readonly variants: readonly RouteVariantKey[];
}

export async function fetchGeometryBatch(
  params: GeometryBatchParams,
  signal?: AbortSignal,
): Promise<GeometryBatchResponse> {
  const response = await apiClient.post<GeometryBatchResponse>(
    '/api/v1/routes/geometry-batch',
    params,
    { signal },
  );
  return response.data;
}

export interface ListRoutesParams {
  readonly limit?: number;
  readonly offset?: number;
  readonly periodFrom?: string | null;
  readonly periodTo?: string | null;
  readonly lengthMinKm?: number | null;
  readonly lengthMaxKm?: number | null;
  readonly hasAppliedBarriers?: boolean | null;
  readonly hasResidualBarriers?: boolean | null;
  readonly search?: string | null;
  readonly sortBy?: RouteSortField;
  readonly sortDir?: RouteSortDirection;
  readonly signal?: AbortSignal;
}

export async function listRoutes(
  params: ListRoutesParams = {},
): Promise<RoutesListResponse> {
  const { signal, ...rest } = params;
  const response = await apiClient.get<RoutesListResponse>('/api/v1/routes', {
    params: serializeListParams(rest),
    signal,
  });
  return response.data;
}

export type ListRouteIdsParams = Omit<
  ListRoutesParams,
  'limit' | 'offset' | 'sortBy' | 'sortDir'
>;

export async function listRouteIds(
  params: ListRouteIdsParams = {},
): Promise<RouteIdsResponse> {
  const { signal, ...rest } = params;
  const response = await apiClient.get<RouteIdsResponse>('/api/v1/routes/ids', {
    params: serializeListParams(rest),
    signal,
  });
  return response.data;
}

export async function bulkDeleteRoutes(
  routeIds: readonly string[],
): Promise<BulkDeleteResponse> {
  const response = await apiClient.post<BulkDeleteResponse>(
    '/api/v1/routes/bulk/delete',
    { route_ids: routeIds },
  );
  return response.data;
}

export async function bulkRecomputeRoutes(
  routeIds: readonly string[],
): Promise<BulkRecomputeResponse> {
  const response = await apiClient.post<BulkRecomputeResponse>(
    '/api/v1/routes/bulk/recompute',
    { route_ids: routeIds },
  );
  return response.data;
}

function serializeListParams(
  params: Omit<ListRoutesParams, 'signal'>,
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  if (params.limit !== undefined) out.limit = params.limit;
  if (params.offset !== undefined) out.offset = params.offset;
  if (params.periodFrom) out.period_from = params.periodFrom;
  if (params.periodTo) out.period_to = params.periodTo;
  if (params.lengthMinKm !== undefined && params.lengthMinKm !== null) {
    out.length_min_km = params.lengthMinKm;
  }
  if (params.lengthMaxKm !== undefined && params.lengthMaxKm !== null) {
    out.length_max_km = params.lengthMaxKm;
  }
  if (params.hasAppliedBarriers !== undefined && params.hasAppliedBarriers !== null) {
    out.has_applied_barriers = params.hasAppliedBarriers;
  }
  if (params.hasResidualBarriers !== undefined && params.hasResidualBarriers !== null) {
    out.has_residual_barriers = params.hasResidualBarriers;
  }
  if (params.search) out.search = params.search;
  if (params.sortBy) out.sort_by = params.sortBy;
  if (params.sortDir) out.sort_dir = params.sortDir;
  return out;
}
